import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../components/ui/table";
import { Search, ExternalLink, RefreshCw } from "lucide-react";
import API_CONSTRUCTOR from "../../../models/api-model";
import { formatDate } from "../../../utils/adminUtils";

export const AdminReports = () => {
  const api = API_CONSTRUCTOR();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  const loadReports = async () => {
    try {
      setLoading(true);
      setError("");
      const query = search.trim()
        ? `/reports?search=${encodeURIComponent(search.trim())}&limit=100`
        : "/reports?limit=100";
      const response = await api.get(query);
      setReports(response?.data || []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load reports");
      setReports([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleReports = useMemo(() => {
    if (!search.trim()) return reports;
    const s = search.toLowerCase();
    return reports.filter((r) =>
      String(r.property_address || "").toLowerCase().includes(s)
    );
  }, [reports, search]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Reports</h2>
          <p className="text-muted-foreground">
            Generated report documents with property and PDF links
          </p>
        </div>
        <Button variant="outline" onClick={loadReports} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search by property address..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Saved Reports ({visibleReports.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading reports...</div>
          ) : visibleReports.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No reports found.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property Name</TableHead>
                    <TableHead>Property Link</TableHead>
                    <TableHead>Report Link</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleReports.map((report) => {
                    const propertyId = report.property_id;
                    const propertyHref = propertyId ? `/property/${propertyId}` : null;
                    const reportHref = report.pdf_url
                      ? `${window.location.origin}${report.pdf_url}`
                      : null;

                    return (
                      <TableRow key={report.id}>
                        <TableCell className="font-medium">
                          {report.property_address || "Property"}
                        </TableCell>
                        <TableCell>
                          {propertyHref ? (
                            <Link
                              to={propertyHref}
                              className="inline-flex items-center text-primary underline"
                              target="_blank"
                              rel="noreferrer"
                            >
                              Open Property
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </Link>
                          ) : (
                            <span className="text-muted-foreground">Not available</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {reportHref ? (
                            <a
                              href={reportHref}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center text-primary underline"
                            >
                              Open Report PDF
                              <ExternalLink className="w-3 h-3 ml-1" />
                            </a>
                          ) : (
                            <span className="text-muted-foreground">Not available</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{report.status || "ready"}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatDate(report.created_at)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminReports;
